import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DailyReportTab } from "./DailyReportTab";
import { EngagementTab } from "./EngagementTab";
import { EventsTab } from "./EventsTab";
import { EventType } from "./eventTypes";
import { OtpFailuresTab } from "./OtpFailuresTab";
import { ReportFilterState } from "./ReportFilters";
import {
  describeRun,
  initialReportParams,
  paramsChanged,
  ReportDefinition,
  ReportKey,
  reportOf,
  ReportParams,
  ResultView,
  withFreshDates,
} from "./reportParams";
import { ReportParamsPane } from "./ReportParamsPane";
import { Reports as RegionTotals } from "./Reports";
import { RetentionTab } from "./RetentionTab";
import { ScorecardTab } from "./ScorecardTab";
import { ScreensTab } from "./ScreensTab";

/** One Run: the parameters it used, kept apart from the ones in the pane. `id` is what re-runs it. */
interface ReportRun {
  id: number;
  params: ReportParams;
}

/** The results of one Run. Re-running remounts it, which is what asks the server again. */
const ReportResults: React.FC<{
  report: ReportKey;
  params: ReportParams;
  view: ResultView;
  onOpenEvents: (type: EventType) => void;
}> = ({ report, params, view, onOpenEvents }) => {
  const filters = params.filters as ReportFilterState;
  switch (report) {
    case "scorecard":
      return <ScorecardTab filters={filters} preset={params.scorecard.preset} custom={params.scorecard.custom} />;
    case "daily":
      return <DailyReportTab dates={params.daily} />;
    case "weekly":
      return <DailyReportTab dates={params.weekly} period="weekly" />;
    case "monthly":
      return <DailyReportTab dates={params.monthly} period="monthly" />;
    case "engagement":
      return <EngagementTab filters={filters} view={view} onOpenEvents={onOpenEvents} />;
    case "retention":
      return <RetentionTab filters={filters} cohortSize={params.retentionCohort} view={view} />;
    case "events":
      return <EventsTab filters={filters} types={params.events.types} member={params.events.member} view={view} />;
    case "screens":
      return <ScreensTab filters={filters} member={params.screens.member} view={view} />;
    case "otp":
      return <OtpFailuresTab filters={filters} channel={params.otp.channel} destination={params.otp.destination.trim()} />;
    case "totals":
      return <RegionTotals dates={params.totals.dates} people={params.totals.people} places={params.totals.places} />;
  }
};

const ViewingPane: React.FC<{
  report: ReportDefinition;
  run: ReportRun | undefined;
  stale: boolean;
  view: ResultView;
  onViewChange: (view: ResultView) => void;
  onOpenEvents: (type: EventType) => void;
}> = ({ report, run, stale, view, onViewChange, onOpenEvents }) => {
  if (!run) {
    return (
      <Paper
        component="section"
        aria-label="Results"
        variant="outlined"
        sx={{ p: 4, textAlign: "center", borderStyle: "dashed", color: "text.secondary" }}
      >
        <Typography variant="body2">Choose your parameters, then click Run. The results show here.</Typography>
      </Paper>
    );
  }

  return (
    <Stack component="section" aria-label="Results" spacing={1.5} sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {describeRun(report.key, run.params)}
          </Typography>
          {stale && (
            <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
              The parameters have changed. Click Run to update these results.
            </Typography>
          )}
        </Box>
        {report.defaultView && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next: ResultView | null) => next && onViewChange(next)}
            aria-label="Show results as"
          >
            <ToggleButton value="chart">Chart</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      <ReportResults key={run.id} report={report.key} params={run.params} view={view} onOpenEvents={onOpenEvents} />
    </Stack>
  );
};

/**
 * The Reports page: pick a report under Reports in the dashboard's menu, its
 * parameters appear, Run shows the results.
 *
 * The pane's values and the last Run's are kept apart, so an edit asks the server
 * nothing and the results keep saying what they were run with. Table or chart is
 * neither: switching it changes no parameter and sends no request.
 *
 * The report is in the URL as `tab`, so a link opens it. Nothing runs by itself.
 */
export const ReportsPage: React.FC = () => {
  const [search, setSearch] = useSearchParams();
  const report = reportOf(search.get("tab"));

  const [params, setParams] = useState<ReportParams>(() => initialReportParams());
  const [runs, setRuns] = useState<Partial<Record<ReportKey, ReportRun>>>({});
  const [views, setViews] = useState<Partial<Record<ReportKey, ResultView>>>({});
  const nextRun = useRef(1);

  const open = (key: ReportKey) => {
    setSearch({ tab: key }, { replace: true });
  };

  const runReport = (key: ReportKey, from: ReportParams) => {
    const fresh = withFreshDates(from);
    setParams(fresh);
    setRuns((current) => ({ ...current, [key]: { id: nextRun.current++, params: fresh } }));
  };

  /**
   * A number on Engagement says who did it. It runs at once, on the dates that
   * number was counted over, whatever the pane has moved to since, and for every
   * member, or the list would not add up to the number clicked.
   */
  const openEvents = (type: EventType) => {
    const counted = runs.engagement?.params ?? params;
    runReport("events", { ...params, filters: counted.filters, events: { types: [type], member: null } });
    open("events");
  };

  const run = runs[report.key];

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Typography variant="h4" component="h1">
        Reports
      </Typography>
      <ReportParamsPane
        key={report.key}
        report={report}
        params={params}
        onChange={setParams}
        onRun={() => runReport(report.key, params)}
      />
      <ViewingPane
        report={report}
        run={run}
        stale={Boolean(run) && paramsChanged(report.key, params, run!.params)}
        view={views[report.key] ?? report.defaultView ?? "table"}
        onViewChange={(view) => setViews((current) => ({ ...current, [report.key]: view }))}
        onOpenEvents={openEvents}
      />
    </Stack>
  );
};
